import { useAtomValue } from "jotai";
import { useMemo } from "react";

import {
  ExtendedCloudApp,
  ExtendedGlobalApp,
} from "../components/GrowthCloudStore/Cards/AppCard";
import useGetCloudApps from "../api/api-hooks/cloudApps/useGetCloudApps";
import useGlobalApps from "../api/api-hooks/globalApps/useGlobalApps";
import useWorkspace from "../api/api-hooks/workspaces/useWorkspace";
import { tenantIDAtom } from "../atoms/GlobalAtoms";
import { GlobalApp } from "../types/GlobalInterfaces";

/**
 * Fetch all the global and cloud apps of the current Tenant
 * with some metadata to be used on the GrowthCloud Store
 */
const useGrowthCloudStore = ({
  disabled,
  searchTerm,
  selectedTags,
}: {
  disabled: boolean;
  searchTerm?: string;
  selectedTags?: string[];
}) => {
  const tenantID = useAtomValue(tenantIDAtom);
  const { workspace } = useWorkspace();
  const { globalApps, loadingApps } = useGlobalApps(disabled);
  const { cloudApps, loadingApps: loadingCloudApps } = useGetCloudApps(
    undefined,
    disabled
  );

  // All of the cloud apps that are GLOBAL.
  const appsInCloud = cloudApps?.flatMap((cApp: any) => {
    return !!cApp?.GlobalAppID ? [cApp.GlobalAppID] : [];
  });

  const appsInWorkspace = useMemo(() => {
    return workspace?.Sidebar?.flatMap((item: any) => {
      return !!item?.GlobalAppID ? [item.GlobalAppID] : [];
    });
  }, [workspace, cloudApps, globalApps]);

  // A map of the app IDs to their current app version in the SIDEBAR
  // We use this to check and see if updates are available.
  const appsVersionsSidebar = useMemo(() => {
    return workspace?.Sidebar?.reduce(
      (result: { [key: string]: string }, item: any) => {
        if (!item?.GlobalAppID) return result;

        result[item.GlobalAppID] = item.GlobalAppVersion || 0;

        return result;
      },
      {}
    );
  }, [workspace, cloudApps, globalApps]);

  const appsVersions = useMemo(() => {
    return cloudApps?.reduce((result: { [key: string]: number }, cApp: any) => {
      if (!cApp?.GlobalAppID) return result;

      result[cApp.GlobalAppID] = cApp.GlobalAppVersion || 0;

      return result;
    }, {});
  }, [cloudApps, cloudApps, globalApps]);

  const mostPopularApps = useMemo(() => {
    // TODO: Calculate most popular apps
    return [];
  }, []);

  const extendedCloudApps = useMemo(() => {
    if (!cloudApps || !cloudApps.length) return [];

    const extendedApps: ExtendedCloudApp[] = [];
    const cloudAppsFiltered = cloudApps.filter(
      // @ts-ignore
      (cloudApp) => !cloudApp.GlobalAppID
    );

    for (let i = 0; i < cloudAppsFiltered.length; i++) {
      const app = cloudAppsFiltered[i];

      const appObject = {
        ...app,
        Icon: app?.Icon?.PublicURL ?? "",
        Name: app.Title,
        isSameCloud: true,
        isInstalledOnWS: true,
        isInstalledOnCloud: true,
        isInstalled: true,
        isInstalledButNotInWS: false,
        isOutdatedSidebar: false,
        isOutdated: false,
        isCloudApp: true,
        buttonDisabled: true,
        buttonText: "Is Cloud App",
        visbility: true,
      };
      extendedApps.push(appObject);
    }

    return filterApps(extendedApps, searchTerm, selectedTags);
  }, [cloudApps, searchTerm, selectedTags]);

  const extendedGlobalApps = useMemo(() => {
    const extendedApps: ExtendedGlobalApp[] = [];

    for (let i = 0; i < globalApps.length; i++) {
      const gApp = globalApps[i];
      const app = { ...gApp, Title: gApp.Name };
      const isSameCloud = app?.OriginCloudID === tenantID;
      const isInstalledOnWS = appsInWorkspace?.includes(app.id);
      const isInstalledOnCloud = appsInCloud?.includes(app.id);

      const isInstalled = isSameCloud || isInstalledOnWS;
      const isInstalledButNotInWS = isInstalledOnCloud && !isInstalledOnWS;

      const isOutdatedSidebar = isSameCloud
        ? false
        : isInstalled && app.Version !== appsVersionsSidebar[app.id];
      const isOutdated = isInstalled && app.Version !== appsVersions?.[app.id];

      let buttonText = isInstalled ? "Uninstall" : "Install";

      // App is installed on cloud, but not available in workspace.
      if (isInstalledButNotInWS) {
        buttonText = "Add To Workspace";
      }

      // App is outdated, and needs to be updated.
      if (isOutdated || isOutdatedSidebar) {
        buttonText = "Update";
      }

      if (isSameCloud) {
        buttonText = "Created on this cloud";
      }

      const appObject = {
        ...app,
        Icon: app?.Icon?.PublicURL ?? "",
        isSameCloud: isSameCloud,
        isInstalledOnWS: isInstalledOnWS,
        isInstalledOnCloud: isInstalledOnCloud,
        isInstalled: isInstalled,
        isInstalledButNotInWS: isInstalledButNotInWS,
        isOutdatedSidebar: isOutdatedSidebar,
        isOutdated: isOutdated,
        isCloudApp: false,
        buttonText: buttonText,
        uninstallFunc: null,
        installFunc: null,
        visibility:
          workspace?.AppsVisibility?.[app.id] === undefined
            ? true
            : workspace?.AppsVisibility?.[app.id],
      };

      extendedApps.push(appObject);
    }

    return filterApps(extendedApps, searchTerm, selectedTags);
  }, [
    globalApps,
    cloudApps,
    appsInCloud,
    appsInWorkspace,
    appsVersionsSidebar,
    appsVersions,
    searchTerm,
    selectedTags,
  ]);

  const pendingUpdateApps = useMemo(() => {
    const apps: ExtendedGlobalApp[] = extendedGlobalApps.filter(
      (extendedApp) => extendedApp.isOutdated
    );

    return filterApps(apps, searchTerm, selectedTags);
  }, [extendedGlobalApps, searchTerm, selectedTags]);

  const allInstalledApps = useMemo(() => {
    const apps: ExtendedGlobalApp[] = extendedGlobalApps.filter(
      (extendedApp) => extendedApp.isInstalled
    );

    return filterApps(apps, searchTerm, selectedTags);
  }, [extendedGlobalApps, searchTerm, selectedTags]);

  const allMSTags = useMemo(() => {
    const tags = new Set<string>();
    globalApps?.forEach((app: GlobalApp) => {
      app.MSTags?.forEach((tag) => tags.add(tag.Title.trim()));
    });
    cloudApps?.forEach((app) => {
      app.MSTags?.forEach((tag) => tags.add(tag.Title.trim()));
    });
    const sortedTags = Array.from(tags).sort();
    return sortedTags;
  }, [globalApps, cloudApps]);

  /**
   * Contains only the installed global apps in the workspace.
   */
  const installedOnWorkspace = useMemo(() => {
    return extendedGlobalApps.filter(
      (extendedApp) => !!extendedApp.isInstalledOnWS
    );
  }, [extendedGlobalApps]);

  return {
    loading: loadingApps || loadingCloudApps,
    extendedCloudApps,
    extendedGlobalApps,
    mostPopularApps,
    allInstalledApps,
    pendingUpdateApps,
    installedOnWorkspace,
    allMSTags,
  };
};

const filterApps = <T extends ExtendedCloudApp | ExtendedGlobalApp>(
  apps: T[] | undefined,
  searchTerm?: string,
  selectedTags?: string[]
): T[] => {
  if (!apps) return [];

  if (!searchTerm && !selectedTags?.length) return apps;

  return apps.filter((app) => {
    const appName = app?.Name ?? "";

    const nameMatch = searchTerm
      ? appName.toLowerCase().trim().includes(searchTerm.toLowerCase().trim())
      : true;

    const tagsMatch = selectedTags?.length
      ? (app.MSTags || []).some((tag) =>
          selectedTags.includes(tag?.Title?.trim())
        )
      : true;

    return nameMatch && tagsMatch;
  });
};

export default useGrowthCloudStore;
